<template>
  <footer
    class="z-0 relative bg-mbzuai-navy-500 text-white border-t-2! border-mbzuai-sand-500! background-pattern-navy-500--footer"
  >
    <section class="z-0 w-full">
      <div class="site-footer">
        <!-- Main lighter band: logo + content + menus + copyright -->
        <ContentContainer class="flex flex-col py-10.5">
          <img
            v-if="logoUrl"
            :src="logoUrl"
            alt="MBZUAI Logo"
            width="200"
            height="56"
            class="mb-6 lg:mb-16 w-full max-w-[150px] h-[42px] lg:max-w-[200px] lg:h-[56px]"
          />

          <section class="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
            <div class="lg:col-span-4 flex flex-col gap-10">
              <!-- Mobile-only: menu accordions stack between logo and newsletter -->
              <div class="footer-accordions lg:hidden">
                <Accordion v-model:value="activeMenuId">
                  <AccordionPanel
                    v-for="(column, idx) in columns"
                    :key="column.id"
                    :value="column.id"
                    :pt="{ root: { class: 'border-mbzuai-sand-100!' } }"
                  >
                    <AccordionHeader
                      v-slot="{ active }"
                      :pt="{
                        toggleicon: { class: 'hidden!' },
                      }"
                    >
                      {{ column.title }}
                      <ChevronIcon
                        class="min-w-4 aspect-square"
                        :direction="active ? 'up' : 'down'"
                        severity="dark"
                      />
                    </AccordionHeader>

                    <AccordionContent>
                      <ul class="flex flex-col gap-3 py-2">
                        <li
                          v-for="item in column.items"
                          :key="`${idx}-${item.href}`"
                        >
                          <NuxtLink
                            :to="item.href"
                            :target="item.external ? '_blank' : undefined"
                            :rel="item.external ? 'noopener' : undefined"
                            class="no-underline! text-body-small text-white/90! transition-colors hover:text-white! focus-visible:outline-none focus-visible:text-mbzuai-dark-navy-500! focus-visible:underline! focus-visible:decoration-white! focus-visible:bg-transparent"
                          >
                            {{ item.title }}
                          </NuxtLink>
                        </li>
                      </ul>
                    </AccordionContent>
                  </AccordionPanel>
                </Accordion>
              </div>

              <div
                v-if="newsletter && enableNewsletterSignup"
                class="flex flex-col gap-3"
              >
                <p class="text-body-large-medium text-mbzuai-sand-500!">
                  {{ newsletter.title }}
                </p>
                <ClientOnly>
                  <form class="relative w-full max-w-sm" @submit.prevent>
                    <InputText
                      name="subscribe"
                      :placeholder="$t('Email')"
                      autocomplete="email"
                      class="footer-newsletter-input w-full rounded-full py-3.5 ps-6 pe-14"
                      :pt="{
                        root: {
                          class: [
                            'bg-white/15 text-white placeholder:text-white/80',
                          ],
                        },
                      }"
                    />
                    <button
                      type="submit"
                      :aria-label="$t('Subscribe')"
                      class="absolute end-1.5 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-mbzuai-sand-500 hover:bg-mbzuai-sand-400 transition-colors text-mbzuai-navy-500 flex items-center justify-center"
                    >
                      <MBZIcon name="arrow" />
                    </button>
                  </form>
                </ClientOnly>
              </div>

              <div class="flex flex-col gap-2">
                <NuxtLink
                  v-if="addressMap"
                  :to="addressMap.href"
                  :target="addressMap.external ? '_blank' : undefined"
                  :rel="addressMap.external ? 'noopener' : undefined"
                  class="no-underline! text-body-large-medium text-mbzuai-sand-500! hover:text-mbzuai-sand-300! transition-colors focus-visible:outline-none focus-visible:text-mbzuai-dark-navy-500! focus-visible:underline! focus-visible:decoration-white! focus-visible:bg-transparent"
                >
                  {{ addressMap.title }}
                </NuxtLink>
                <address
                  v-if="addressHtml"
                  class="text-body-large text-white not-italic"
                >
                  <DrupalMarkup :content="addressHtml" />
                </address>
              </div>
            </div>

            <!-- Desktop-only: 4 menu columns -->
            <nav
              class="hidden lg:grid lg:col-span-8 lg:grid-cols-4 lg:gap-8"
              :aria-label="$t('Footer navigation')"
            >
              <div
                v-for="(column, idx) in columns"
                :key="column.id"
                class="flex flex-col gap-4"
              >
                <p class="text-body-large-medium text-mbzuai-sand-500!">
                  {{ column.title }}
                </p>
                <ul class="flex flex-col gap-3">
                  <li v-for="item in column.items" :key="`${idx}-${item.href}`">
                    <NuxtLink
                      :to="item.href"
                      :target="item.external ? '_blank' : undefined"
                      :rel="item.external ? 'noopener' : undefined"
                      class="no-underline! text-body-small text-white/90! transition-colors hover:text-white! focus-visible:outline-none focus-visible:text-mbzuai-dark-navy-500! focus-visible:underline! focus-visible:decoration-white! focus-visible:bg-transparent"
                    >
                      {{ item.title }}
                    </NuxtLink>
                  </li>
                </ul>
              </div>
            </nav>
          </section>

          <div class="h-px mt-10 lg:mt-14 bg-mbzuai-sand-100" />

          <div
            class="mt-8 flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center lg:justify-between lg:gap-x-8 lg:gap-y-4"
          >
            <ul
              class="flex flex-wrap gap-x-6 gap-y-2 text-body-small text-white/90"
            >
              <li v-for="item in policyLinks" :key="item.href">
                <NuxtLink
                  :to="item.href"
                  :target="item.external ? '_blank' : undefined"
                  :rel="item.external ? 'noopener' : undefined"
                  class="no-underline! transition-colors hover:text-white focus-visible:outline-none focus-visible:text-mbzuai-dark-navy-500! focus-visible:underline! focus-visible:decoration-white! focus-visible:bg-transparent"
                >
                  {{ item.title }}
                </NuxtLink>
              </li>
            </ul>

            <p
              v-if="copyright"
              class="pb-4 text-body-small text-mbzuai-sand-500! lg:order-last lg:w-full"
            >
              {{ copyright }}
            </p>

            <SocialLinks
              v-if="socialLinks.length"
              :links="socialLinks"
              class="lg:mt-[5px]"
            />
          </div>
        </ContentContainer>
      </div>
    </section>
  </footer>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import type { Link, SocialLink } from '~/types';

// Maps BE-provided social-media ids to the corresponding SVG filename under
// /public/images/social/<icon>.svg. `twitter` is folded into `x` since the
// brand changed and the only on-disk asset is x.svg.
const SOCIAL_ICON_MAP: Record<string, string> = {
  instagram: 'instagram',
  facebook: 'facebook',
  linkedin: 'linkedinBadge',
  x: 'x',
  twitter: 'x',
  tiktok: 'tiktok',
  youtube: 'youtube',
};

const SOCIAL_LABEL_MAP: Record<string, string> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  x: 'X (Twitter)',
  twitter: 'X (Twitter)',
  tiktok: 'TikTok',
  youtube: 'YouTube',
};

type FooterNavSection = {
  props?: { title?: string; url?: Link[] };
};
type FooterNewsletterBlock = {
  props?: { title?: string; form?: string };
};
type FooterSocialItem = {
  props?: { socialMedia?: string; socialUrl?: Link };
};
type FooterBlock = {
  props?: {
    copyright?: string;
    enableNewsletterSignup?: string;
    policyLinks?: Link[];
    universityAddressMap?: Link;
  };
  slots?: {
    navigationSections?: FooterNavSection[];
    newsletterBlock?: FooterNewsletterBlock[];
    socialMedia?: FooterSocialItem[];
    universityAddress?: string | string[];
  };
};

const { getPage } = useDrupalCe();
const page = getPage();

// Drupal serves the footer block under blocks.footer.claro_footer regardless
// of admin login state — `claro_footer` is the region slot name, not Claro
// admin chrome.
const footer = computed<FooterBlock | null>(
  () => page.value?.blocks?.footer?.claro_footer ?? null
);

// Footer sits on the dark navy band — use the light logo variant.
const logoUrl = computed<string>(() => page.value?.logo?.light?.url ?? '');

const footerProps = computed(() => footer.value?.props ?? {});
const footerSlots = computed(() => footer.value?.slots ?? {});

const copyright = computed<string>(() => footerProps.value.copyright ?? '');
const enableNewsletterSignup = computed(
  () => footerProps.value.enableNewsletterSignup === '1'
);
const policyLinks = computed<Link[]>(() => footerProps.value.policyLinks ?? []);
const addressMap = computed<Link | null>(
  () => footerProps.value.universityAddressMap ?? null
);

const columns = computed(() =>
  (footerSlots.value.navigationSections ?? []).map((section, idx) => ({
    id: `${section.props?.title ?? 'col'}-${idx}`,
    title: section.props?.title ?? '',
    items: (section.props?.url ?? []) as Link[],
  }))
);

const newsletter = computed(() => {
  const block = (footerSlots.value.newsletterBlock ?? [])[0];
  if (!block) return null;
  return {
    title: block.props?.title ?? '',
    form: block.props?.form ?? '',
  };
});

// Drop any BE-sent socials whose id isn't in SOCIAL_ICON_MAP — we'd otherwise
// render an <img> pointing at a missing /images/social/<id>.svg and ship a
// broken-image icon. Anything not on the FE allow-list is silently filtered.
const socialLinks = computed<SocialLink[]>(() =>
  (footerSlots.value.socialMedia ?? [])
    .map((item, idx) => {
      const id: string = item.props?.socialMedia ?? `social-${idx}`;
      const url: string = item.props?.socialUrl?.href ?? '';
      const icon = SOCIAL_ICON_MAP[id];
      if (!url || !icon) return null;
      return {
        id,
        url,
        icon,
        ariaLabel: SOCIAL_LABEL_MAP[id] ?? id,
      } satisfies SocialLink;
    })
    .filter((x): x is SocialLink => x !== null)
);

const addressHtml = computed<string>(() => {
  const raw = footerSlots.value.universityAddress;
  if (!raw) return '';
  const joined = Array.isArray(raw) ? raw.join('') : String(raw);
  // BE delimits address lines with U+2028 (LINE SEPARATOR) / U+2029. CSS
  // doesn't treat those as segment breaks, so promote them to <br>.
  return joined.replace(/[\u2028\u2029]/g, '<br>');
});

// Single-open accordion (mobile). `null` keeps all panels collapsed.
const activeMenuId = ref<string | null>(null);
</script>

<style>
/* PrimeVue Accordion theme override for the dark footer.
   Override CSS tokens so each panel reads transparently against the navy
   footer background and headers pick up the sand-200 brand colour. */
.footer-accordions {
  --p-accordion-panel-background: transparent;
  --p-accordion-panel-border-color: var(--color-mbzuai-sand-200);
  --p-accordion-header-background: transparent;
  --p-accordion-header-hover-background: transparent;
  --p-accordion-header-active-background: transparent;
  --p-accordion-header-active-hover-background: transparent;
  --p-accordion-header-color: var(--color-mbzuai-sand-500);
  --p-accordion-header-hover-color: var(--color-mbzuai-sand-300);
  --p-accordion-header-active-color: var(--color-mbzuai-sand-300);
  --p-accordion-header-active-hover-color: var(--color-mbzuai-sand-300);
  --p-accordion-header-padding: 1rem 0;
  --p-accordion-header-font-weight: 500;
  --p-accordion-content-background: transparent;
  --p-accordion-content-color: rgb(255 255 255 / 0.9);
  --p-accordion-content-padding: 0 0 1rem;
  /* PrimeVue 4 names the toggle-icon tokens with the `header-toggle-icon`
     prefix — the shorter `toggle-icon` form does nothing and let the chevron
     fall back to its default zinc-500. */
  --p-accordion-header-toggle-icon-color: var(--color-mbzuai-sand-500);
  --p-accordion-header-toggle-icon-hover-color: var(--color-mbzuai-sand-300);
  --p-accordion-header-toggle-icon-active-color: var(--color-mbzuai-sand-300);
}

/* Newsletter input: drop the default InputText border/shadow so only the
   semi-transparent fill shows against the navy panel. PrimeVue applies its
   sand-100 border via design tokens, so `border-transparent` on the element
   alone doesn't win — override the relevant tokens scoped to this input. */
.footer-newsletter-input {
  --p-inputtext-border-color: transparent;
  --p-inputtext-hover-border-color: transparent;
  --p-inputtext-focus-border-color: transparent;
  --p-inputtext-shadow: none;
}

@media screen and (min-width: 1024px) {
  .site-footer {
    animation: scale-in ease-in-out both;
    animation-timeline: scroll();
    animation-range: 90% 98%;
    transform-style: preserve-3d;
    transition: all;
    /* Resting state is fully revealed. When the page is too short to scroll,
       the scroll() timeline is inactive and the animation produces no effect,
       so the footer stays visible. The hidden start state lives in the
       keyframes and only applies while the timeline is active (longer pages). */
    opacity: 1;
    transform: translate3d(0px, 0px, 0px) scale3d(1, 1, 1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .site-footer {
    animation: none;
    animation-timeline: none;
    opacity: 1;
    transform: translate3d(0px, 0px, 0px) scale3d(1, 1, 1);
  }
}

@keyframes scale-in {
  from {
    opacity: 0;
    transform: translate3d(0px, 0px, 0px) scale3d(0.9, 0.9, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0px, 0px, 0px) scale3d(1, 1, 1);
  }
}
</style>
